export const APP_NAV_EVENT = 'app:navigate';
/** sessionStorage key: full path (pathname + search + hash) to restore after SSO */
export const AUTH_RETURN_PATH_KEY = 'blend.auth.returnPath';
export const DEFAULT_CLIENT_ID = 'client-1';
export const DEFAULT_PROJECT_ID = 'nexus-health';
// Backward-compatible alias for legacy call sites/tests.
export const DEFAULT_ASSESSMENT_ID = DEFAULT_PROJECT_ID;

export type AppRouteKey =
  | 'login'
  | 'callback'
  | 'portfolio'
  | 'projects'
  | 'project-detail'
  | 'project-weekly-updates'
  | 'project-weekly-update-create'
  | 'project-weekly-update-edit'
  | 'project-weekly-update-view'
  | 'project-assessments'
  | 'asset-library'
  | 'asset-library-note'
  | 'file-cabinet'
  | 'skills'
  | 'skill-edit'
  | 'skill-template'
  | 'step-1'
  | 'step-1-generating'
  | 'step-1-output'
  | 'step-1-review'
  | 'step-2'
  | 'step-2-generating'
  | 'step-2-output'
  | 'step-3'
  | 'step-3-generating'
  | 'step-3-output'
  | 'step-4'
  | 'step-4-generating'
  | 'step-4-output'
  | 'workspace'
  | 'settings-profile'
  | 'not-found';

export interface AppRouteMatch {
  key: AppRouteKey;
  pathname: string;
  clientId?: string;
  projectId?: string;
  assessmentId?: string;
  /** Present for `/clients/.../projects/.../assessments/:assessmentInstanceId/...` routes. */
  assessmentInstanceId?: string;
  assetSessionId?: string;
  /** Weekly update week parameter for `/clients/.../projects/.../weekly-updates/:week` routes. */
  week?: string;
  /** Skill library: persisted version id (UUID) for editor route. */
  skillVersionId?: string;
  /** Skill library: canonical template key for template preview route. */
  skillTemplateKey?: string;
}

export const appRoutes = {
  login: '/login',
  callback: '/callback',
  portfolio: '/portfolio',
  legacyFileCabinet: '/file-cabinet',
  clientProjects: (clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/`,
  projects: (clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects`,
  projectHome: (clientId: string = DEFAULT_CLIENT_ID, projectId: string = DEFAULT_PROJECT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/`,
  projectWeeklyUpdates: (
    clientId: string = DEFAULT_CLIENT_ID,
    projectId: string = DEFAULT_PROJECT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/weekly-updates`,
  projectWeeklyUpdateCreate: (
    clientId: string = DEFAULT_CLIENT_ID,
    projectId: string = DEFAULT_PROJECT_ID,
    week: string = '',
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/weekly-updates/${encodeURIComponent(week)}/create`,
  projectWeeklyUpdateEdit: (
    clientId: string = DEFAULT_CLIENT_ID,
    projectId: string = DEFAULT_PROJECT_ID,
    week: string = '',
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/weekly-updates/${encodeURIComponent(week)}/edit`,
  projectWeeklyUpdateView: (
    clientId: string = DEFAULT_CLIENT_ID,
    projectId: string = DEFAULT_PROJECT_ID,
    week: string = '',
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/weekly-updates/${encodeURIComponent(week)}/view`,
  clientAssetLibrary: (clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/asset_library`,
  projectAssessments: (
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments`,
  projectAssetLibrary: (
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/asset_library`,
  projectAssetLibrarySession: (
    assetSessionId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/asset_library/${encodeURIComponent(assetSessionId)}`,
  fileCabinet: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/file-cabinet`,
  projectAssessmentStep1: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-1`,
  projectAssessmentStep1Generating: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-1/generating`,
  projectAssessmentStep1Output: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-1/output`,
  projectAssessmentStep1Review: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-1/review`,
  projectAssessmentStep2: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-2`,
  projectAssessmentStep2Generating: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-2/generating`,
  projectAssessmentStep2Output: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-2/output`,
  projectAssessmentStep3: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-3`,
  projectAssessmentStep3Generating: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-3/generating`,
  projectAssessmentStep3Output: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-3/output`,
  projectAssessmentStep4: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-4`,
  projectAssessmentStep4Generating: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-4/generating`,
  projectAssessmentStep4Output: (
    assessmentInstanceId: string,
    projectId: string = DEFAULT_PROJECT_ID,
    clientId: string = DEFAULT_CLIENT_ID,
  ) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments/${encodeURIComponent(assessmentInstanceId)}/step-4/output`,
  step1: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-1`,
  step1Generating: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-1/generating`,
  step1Output: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-1/output`,
  step2: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-2`,
  step2Generating: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-2/generating`,
  step2Output: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-2/output`,
  step3: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-3`,
  step3Generating: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-3/generating`,
  step3Output: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-3/output`,
  step4: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-4`,
  step4Generating: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-4/generating`,
  step4Output: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/step-4/output`,
  workspace: (projectId: string = DEFAULT_PROJECT_ID, clientId: string = DEFAULT_CLIENT_ID) =>
    `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/workspace`,
  settings: '/settings',
  settingsProfile: '/settings/profile',
  /** Global Skill Library (not scoped to client/project in the URL). */
  skills: '/skills',
  skillEdit: (versionId: string) => `/skills/edit/${encodeURIComponent(versionId)}`,
  skillTemplate: (templateKey: string) => `/skills/template/${encodeURIComponent(templateKey)}`,
} as const;

const RE_SCOPED_PROJECT_HOME = /^\/clients\/([^/]+)\/projects\/([^/]+)\/?$/;
const RE_PROJECT_WEEKLY_UPDATES = /^\/clients\/([^/]+)\/projects\/([^/]+)\/weekly-updates$/;
const RE_PROJECT_WEEKLY_UPDATE_CREATE =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/weekly-updates\/([^/]+)\/create\/?$/;
const RE_PROJECT_WEEKLY_UPDATE_EDIT =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/weekly-updates\/([^/]+)\/edit\/?$/;
const RE_PROJECT_WEEKLY_UPDATE_VIEW =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/weekly-updates\/([^/]+)\/view\/?$/;
const RE_SCOPED_PROJECTS_LIST = /^\/clients\/([^/]+)\/projects$/;
const RE_PROJECT_ASSESSMENTS = /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments$/;
const RE_PROJECT_ASSET_LIBRARY = /^\/clients\/([^/]+)\/projects\/([^/]+)\/asset_library$/;
const RE_PROJECT_ASSET_LIBRARY_SESSION =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/asset_library\/([^/]+)\/?$/;
const RE_SCOPED_FILE_CABINET = /^\/clients\/([^/]+)\/projects\/([^/]+)\/file-cabinet$/;
const RE_SKILLS = /^\/skills\/?$/;
const RE_SKILL_EDIT = /^\/skills\/edit\/([^/]+)\/?$/;
const RE_SKILL_TEMPLATE = /^\/skills\/template\/([^/]+)\/?$/;
const RE_PROJECT_ASSESSMENT_STEP1 =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-1$/;
const RE_PROJECT_ASSESSMENT_STEP1_GEN =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-1\/generating$/;
const RE_PROJECT_ASSESSMENT_STEP1_OUT =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-1\/output$/;
const RE_PROJECT_ASSESSMENT_STEP1_REVIEW =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-1\/review$/;
const RE_PROJECT_ASSESSMENT_STEP2 =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-2$/;
const RE_PROJECT_ASSESSMENT_STEP2_GEN =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-2\/generating$/;
const RE_PROJECT_ASSESSMENT_STEP2_OUT =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-2\/output$/;
const RE_PROJECT_ASSESSMENT_STEP3 =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-3$/;
const RE_PROJECT_ASSESSMENT_STEP3_GEN =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-3\/generating$/;
const RE_PROJECT_ASSESSMENT_STEP3_OUT =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-3\/output$/;
const RE_PROJECT_ASSESSMENT_STEP4 =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-4$/;
const RE_PROJECT_ASSESSMENT_STEP4_GEN =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-4\/generating$/;
const RE_PROJECT_ASSESSMENT_STEP4_OUT =
  /^\/clients\/([^/]+)\/projects\/([^/]+)\/assessments\/([^/]+)\/step-4\/output$/;
const RE_STEP1 = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-1$/;
const RE_STEP1_GEN = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-1\/generating$/;
const RE_STEP1_OUT = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-1\/output$/;
const RE_STEP2 = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-2$/;
const RE_STEP2_GEN = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-2\/generating$/;
const RE_STEP2_OUT = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-2\/output$/;
const RE_STEP3 = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-3$/;
const RE_STEP3_GEN = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-3\/generating$/;
const RE_STEP3_OUT = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-3\/output$/;
const RE_STEP4 = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-4$/;
const RE_STEP4_GEN = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-4\/generating$/;
const RE_STEP4_OUT = /^\/clients\/([^/]+)\/projects\/([^/]+)\/step-4\/output$/;
const RE_WORKSPACE = /^\/clients\/([^/]+)\/projects\/([^/]+)\/workspace$/;

const RE_LEGACY_STEP1 = /^\/assessment\/([^/]+)\/step-1$/;
const RE_LEGACY_STEP1_GEN = /^\/assessment\/([^/]+)\/step-1\/generating$/;
const RE_LEGACY_STEP1_OUT = /^\/assessment\/([^/]+)\/step-1\/output$/;
const RE_LEGACY_STEP2 = /^\/assessment\/([^/]+)\/step-2$/;
const RE_LEGACY_STEP2_GEN = /^\/assessment\/([^/]+)\/step-2\/generating$/;
const RE_LEGACY_STEP2_OUT = /^\/assessment\/([^/]+)\/step-2\/output$/;
const RE_LEGACY_STEP3 = /^\/assessment\/([^/]+)\/step-3$/;
const RE_LEGACY_STEP3_GEN = /^\/assessment\/([^/]+)\/step-3\/generating$/;
const RE_LEGACY_STEP3_OUT = /^\/assessment\/([^/]+)\/step-3\/output$/;
const RE_LEGACY_STEP4 = /^\/assessment\/([^/]+)\/step-4$/;
const RE_LEGACY_STEP4_GEN = /^\/assessment\/([^/]+)\/step-4\/generating$/;
const RE_LEGACY_STEP4_OUT = /^\/assessment\/([^/]+)\/step-4\/output$/;
const RE_LEGACY_WORKSPACE = /^\/assessment\/([^/]+)\/workspace$/;

function decodeSeg(value: string): string {
  return decodeURIComponent(value);
}

/** Remember current app URL before sending the user to login or IdP (skipped for login/callback/unknown). */
export const saveAuthReturnPath = (): void => {
  const pathOnly = globalThis.location.pathname;
  if (pathOnly === appRoutes.login || pathOnly === appRoutes.callback || pathOnly === '/') {
    return;
  }
  const match = matchAppRoute(pathOnly);
  if (match.key === 'not-found') {
    return;
  }
  const full = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
  sessionStorage.setItem(AUTH_RETURN_PATH_KEY, full);
};

/** Pop stored return URL if it is a safe in-app route; otherwise null. */
export const consumeAuthReturnPath = (): string | null => {
  const raw = sessionStorage.getItem(AUTH_RETURN_PATH_KEY);
  sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return null;
  }
  const pathOnly = raw.split('?')[0].split('#')[0];
  const match = matchAppRoute(pathOnly);
  if (match.key === 'login' || match.key === 'callback' || match.key === 'not-found') {
    return null;
  }
  return raw;
};

/** True when a click should open a new tab (Ctrl/Cmd/Shift or middle button) instead of navigating here. */
export const isNewTabClick = (event: {
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): boolean => event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1;

export const navigateTo = (pathname: string, replace: boolean = false): void => {
  const nextPath = pathname || appRoutes.portfolio;
  const current = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
  if (current === nextPath) {
    return;
  }

  if (replace) {
    globalThis.history.replaceState({}, '', nextPath);
  } else {
    globalThis.history.pushState({}, '', nextPath);
  }
  globalThis.dispatchEvent(new Event(APP_NAV_EVENT));
};

function matchStaticAppRoutes(pathname: string): AppRouteMatch | null {
  if (pathname === appRoutes.login || pathname === '/') {
    return { key: 'login', pathname };
  }
  if (pathname === appRoutes.callback) {
    return { key: 'callback', pathname };
  }
  if (pathname === appRoutes.portfolio) {
    return { key: 'portfolio', pathname };
  }
  if (pathname === appRoutes.legacyFileCabinet) {
    return {
      key: 'file-cabinet',
      pathname,
      clientId: DEFAULT_CLIENT_ID,
      projectId: DEFAULT_PROJECT_ID,
      assessmentId: DEFAULT_PROJECT_ID,
    };
  }
  if (pathname === appRoutes.settingsProfile) {
    return { key: 'settings-profile', pathname };
  }
  if (pathname === appRoutes.settings) {
    return { key: 'settings-profile', pathname };
  }
  return null;
}

function matchGlobalSkillsRoutes(pathname: string): AppRouteMatch | null {
  let matched = RE_SKILL_EDIT.exec(pathname);
  if (matched) {
    return {
      key: 'skill-edit',
      pathname,
      skillVersionId: decodeSeg(matched[1]),
    };
  }
  matched = RE_SKILL_TEMPLATE.exec(pathname);
  if (matched) {
    return {
      key: 'skill-template',
      pathname,
      skillTemplateKey: decodeSeg(matched[1]),
    };
  }
  matched = RE_SKILLS.exec(pathname);
  if (matched) {
    return { key: 'skills', pathname };
  }
  return null;
}

function matchClientScopedRoutes(pathname: string): AppRouteMatch | null {
  const matchers: Array<{
    re: RegExp;
    build: (m: RegExpExecArray, p: string) => AppRouteMatch;
  }> = [
    {
      re: RE_SCOPED_PROJECT_HOME,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'project-detail',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_PROJECT_WEEKLY_UPDATE_CREATE,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'project-weekly-update-create',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          week: decodeSeg(m[3]),
        };
      },
    },
    {
      re: RE_PROJECT_WEEKLY_UPDATE_EDIT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'project-weekly-update-edit',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          week: decodeSeg(m[3]),
        };
      },
    },
    {
      re: RE_PROJECT_WEEKLY_UPDATE_VIEW,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'project-weekly-update-view',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          week: decodeSeg(m[3]),
        };
      },
    },
    {
      re: RE_PROJECT_WEEKLY_UPDATES,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'project-weekly-updates',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_SCOPED_PROJECTS_LIST,
      build: (m, p) => ({ key: 'projects', pathname: p, clientId: decodeSeg(m[1]) }),
    },
    {
      re: RE_PROJECT_ASSESSMENTS,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'project-assessments',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSET_LIBRARY,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'asset-library',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSET_LIBRARY_SESSION,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'asset-library-note',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assetSessionId: decodeSeg(m[3]),
        };
      },
    },
    {
      re: RE_SCOPED_FILE_CABINET,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'file-cabinet',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP1,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-1',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP1_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-1-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP1_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-1-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP1_REVIEW,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-1-review',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP2,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-2',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP2_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-2-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP2_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-2-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP3,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-3',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP3_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-3-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP3_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-3-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP4,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-4',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP4_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-4-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_PROJECT_ASSESSMENT_STEP4_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        const assessmentInstanceId = decodeSeg(m[3]);
        return {
          key: 'step-4-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
          assessmentInstanceId,
        };
      },
    },
    {
      re: RE_STEP1,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-1',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP1_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-1-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP1_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-1-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP2,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-2',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP2_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-2-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP2_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-2-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP3,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-3',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP3_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-3-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP3_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-3-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP4,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-4',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP4_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-4-generating',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_STEP4_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'step-4-output',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_WORKSPACE,
      build: (m, p) => {
        const projectId = decodeSeg(m[2]);
        return {
          key: 'workspace',
          pathname: p,
          clientId: decodeSeg(m[1]),
          projectId,
          assessmentId: projectId,
        };
      },
    },
  ];

  for (const { re, build } of matchers) {
    const matched = re.exec(pathname);
    if (matched) {
      return build(matched, pathname);
    }
  }
  return null;
}

function matchLegacyAssessmentRoutes(pathname: string): AppRouteMatch | null {
  const matchers: Array<{
    re: RegExp;
    build: (m: RegExpExecArray, p: string) => AppRouteMatch;
  }> = [
    {
      re: RE_LEGACY_STEP1,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-1',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP1_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-1-generating',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP1_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-1-output',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP2,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-2',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP2_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-2-generating',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP2_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-2-output',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP3,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-3',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP3_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-3-generating',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP3_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-3-output',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP4,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-4',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP4_GEN,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-4-generating',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_STEP4_OUT,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'step-4-output',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
    {
      re: RE_LEGACY_WORKSPACE,
      build: (m, p) => {
        const projectId = decodeSeg(m[1]);
        return {
          key: 'workspace',
          pathname: p,
          clientId: DEFAULT_CLIENT_ID,
          projectId,
          assessmentId: projectId,
        };
      },
    },
  ];

  for (const { re, build } of matchers) {
    const matched = re.exec(pathname);
    if (matched) {
      return build(matched, pathname);
    }
  }
  return null;
}

export const matchAppRoute = (pathname: string): AppRouteMatch => {
  return (
    matchStaticAppRoutes(pathname) ??
    matchGlobalSkillsRoutes(pathname) ??
    matchClientScopedRoutes(pathname) ??
    matchLegacyAssessmentRoutes(pathname) ?? { key: 'not-found', pathname }
  );
};
